// ----------------------------------------------------------------------------
// Product Gallery Module: Gallery Video
// ----------------------------------------------------------------------------
// Drives muted autoplay for Shopify-hosted videos in the metafield-driven
// product gallery (variant_gallery / featured_media_desktop). Replaces the
// legacy Plyr-based productVideo module, which targeted the old markup
// (`.product__feature-image`) and no longer matches this layout.
//
// Playback strategy by surface (set via `data-gallery-context` on the wrapper):
// - swiper : play the active slide's video, pause the rest, on Swiper slide
//            change — parity with the legacy gallery's swipe-to-play behaviour.
// - hero   : muted autoplay on scroll-into-view (IntersectionObserver).
// - grid   : muted autoplay on scroll-into-view (IntersectionObserver).
//
// All playback is forced muted + playsinline so browsers permit programmatic
// autoplay.
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:gallery:init'                  → Bootstraps the controller.
// - 'productForm:gallery:featureSlider:ready'   → Re-scans after the swiper
//                                                 (re)initialises, incl. after
//                                                 an AJAX variant gallery swap.
// - 'productForm:gallery:featureSlider:changed' → Plays/pauses swiper videos.
// ----------------------------------------------------------------------------
// Gotchas:
// - Grid videos live in `.product__below`, OUTSIDE <iam-product-gallery>, so
//   queries are scoped to the closest `.product` root, not the gallery element.
// - Hidden surfaces (mobile swiper on desktop, grid/hero on mobile) are
//   `display:none`; we skip play() on elements with no offsetParent, and the
//   IntersectionObserver never reports zero-size elements as intersecting.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';

// -------------------------------------
// Constants
// -------------------------------------

var VIDEO_WRAPPER = '[data-product-media-type-video]';
var OBSERVER_OPTIONS = {
  root: null,
  rootMargin: '0px',
  threshold: 0
};

// -------------------------------------
// Gallery Video Class
// -------------------------------------

class GalleryVideo {
  constructor(iamProductGallery) {
    this.gallery = iamProductGallery;
    this.productRoot = iamProductGallery.closest('.product') || document;
    this.swiperVideos = new Map(); // slide index → <video>

    this.observer = new IntersectionObserver(this.onIntersect.bind(this), OBSERVER_OPTIONS);
    iamotaEventHandler.subscribe('productForm:gallery:featureSlider:ready', this.mountAll.bind(this), {
      context: this.gallery,
      source: "galleryVideo.js"
    });
    iamotaEventHandler.subscribe('productForm:gallery:featureSlider:changed', this.onSlideChanged.bind(this), {
      context: this.gallery,
      source: "galleryVideo.js"
    });
    this.mountAll();
  }

  /**
   * (Re)scan all gallery video tiles. Safe to call repeatedly — observers are
   * reset each time, so it also handles AJAX gallery replacement.
   */
  mountAll() {
    this.observer.disconnect();
    this.swiperVideos.clear();
    this.productRoot.querySelectorAll(VIDEO_WRAPPER).forEach(wrapper => {
      var video = wrapper.querySelector('video');
      if (!video) return;

      // Required for programmatic autoplay across browsers.
      video.muted = true;
      video.playsInline = true;
      video.loop = wrapper.dataset.enableVideoLooping !== 'false';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      if (wrapper.dataset.galleryContext === 'swiper') {
        var slide = wrapper.closest('.swiper-slide');
        var index = slide && slide.parentElement ? Array.prototype.indexOf.call(slide.parentElement.children, slide) : -1;
        if (index >= 0) this.swiperVideos.set(index, video);
      } else {
        // hero + grid: play when scrolled into view.
        this.observer.observe(wrapper);
      }
    });
    this.playActiveSwiperVideo();
  }
  onIntersect(entries) {
    entries.forEach(entry => {
      var video = entry.target.querySelector('video');
      if (!video) return;
      if (entry.isIntersecting) {
        this.play(video);
      } else {
        video.pause();
      }
    });
  }
  onSlideChanged(_ref) {
    var swiper = _ref.swiper;
    var current = this.swiperVideos.get(swiper.activeIndex);
    this.swiperVideos.forEach((video, index) => {
      if (index !== swiper.activeIndex) video.pause();
    });
    if (current) this.play(current);
  }
  playActiveSwiperVideo() {
    var _this$gallery$feature;
    if (!this.swiperVideos.size) return;
    var swiper = (_this$gallery$feature = this.gallery.featureSlider) === null || _this$gallery$feature === void 0 ? void 0 : _this$gallery$feature.featureSlider;
    var index = swiper ? swiper.activeIndex : 0;
    var video = this.swiperVideos.get(index);
    if (video) this.play(video);
  }

  /**
   * Play a video, guarding against hidden surfaces and autoplay rejections.
   */
  play(video) {
    if (video.offsetParent === null) return; // display:none surface
    video.muted = true;
    var played = video.play();
    if (played && typeof played.catch === 'function') played.catch(() => {});
  }
}

// -------------------------------------
// Bootstrap (priority 200 → runs after the feature slider, so the swiper exists)
// -------------------------------------

iamotaEventHandler.subscribe('productForm:gallery:init', _ref2 => {
  var iamProductGallery = _ref2.iamProductGallery;
  new GalleryVideo(iamProductGallery);
}, {
  priority: 200,
  source: "galleryVideo.js"
});